"use client";

import { useContext } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import { FormFunnelHeader } from "@portal/app/_components/formFunnel/FormFunnelHeader";
import { boundProgress } from "@portal/app/_components/formFunnel/utils/funnelProgress";
import { DreamLogo } from "@portal/app/_components/ui-elements/DreamLogo";
import type { PageChromeBlockProps, PageChromeContext } from "../pageChromeTypes";
import { PageFlowContext } from "../PageFlowContext";

export function PageHeader({
  variant,
  ctaLabel,
  sticky,
  showProgress,
  progressPercent = 0,
  inert = true,
  onFormFocus,
  topOffset = 0,
}: PageChromeBlockProps["PageHeader"] & PageChromeContext) {
  const flow = useContext(PageFlowContext);
  progressPercent = flow?.progressPercent ?? progressPercent;
  topOffset = flow?.topOffset ?? topOffset;
  return (
    <Box
      component="header"
      data-testid="landing-page-header"
      data-variant={variant}
      sx={{
        position: sticky ? "sticky" : "relative",
        top: topOffset,
        zIndex: (theme) => theme.zIndex.appBar,
        width: "100%",
        bgcolor: "common.white",
      }}
    >
      {variant === "centered" ? (
        <Box
          sx={{
            "& > [data-testid='form-funnel-header']": { position: "static" },
            ...(!showProgress
              ? { "& [data-testid='funnel-progress-bar']": { display: "none" } }
              : {}),
          }}
        >
          <FormFunnelHeader progressPercent={progressPercent} logoClickable={!inert} />
        </Box>
      ) : (
        <CampaignHeader
          ctaLabel={ctaLabel}
          showProgress={showProgress}
          progressPercent={progressPercent}
          inert={inert}
          onFormFocus={onFormFocus}
        />
      )}
    </Box>
  );
}

function CampaignHeader({
  ctaLabel,
  showProgress,
  progressPercent = 0,
  inert,
  onFormFocus,
}: Pick<PageChromeBlockProps["PageHeader"], "ctaLabel" | "showProgress"> & PageChromeContext) {
  return (
    <Box sx={{ borderBottom: "1px solid #e6e9f2" }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          maxWidth: "1368px",
          py: "12px",
          px: "16px",
          "& img": { display: "block" },
          "@media (min-width: 480px)": { gap: "12px" },
          "@media (min-width: 768px)": { py: "16px", px: "24px", gap: "28px" },
        }}
      >
        <DreamLogo
          width={237}
          clickable={!inert}
          sx={{
            flexShrink: 0,
            height: "20px !important",
            "& a": { display: "block" },
            "& img": { width: "auto", height: "20px" },
            "@media (min-width: 480px)": {
              height: "24px !important",
              "& img": { height: "24px" },
            },
            "@media (min-width: 768px)": {
              height: "34px !important",
              "& img": { height: "34px" },
            },
          }}
        />
        {ctaLabel.trim() && (
          <Button
            type="button"
            variant="contained"
            aria-disabled={inert || undefined}
            tabIndex={inert ? -1 : undefined}
            onClick={inert ? undefined : onFormFocus}
            sx={{
              flexShrink: 1,
              minWidth: 0,
              overflowWrap: "anywhere",
              whiteSpace: "normal",
              borderRadius: "999px",
              "&&&": {
                borderRadius: "999px",
                minHeight: "44px",
                px: "12px",
                py: "8px",
                fontSize: "13px",
                lineHeight: "28px",
                "@media (min-width: 480px)": { px: "14px", fontSize: "14px" },
                "@media (min-width: 768px)": {
                  minHeight: "50px",
                  px: "24px",
                  py: "11px",
                  fontSize: "18px",
                },
              },
              fontWeight: 600,
              bgcolor: "#0b2ea1",
              "&:hover": { bgcolor: "#0b2ea1" },
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            {ctaLabel}
          </Button>
        )}
      </Container>
      {showProgress && (
        <LinearProgress
          aria-label="Matching progress"
          variant="determinate"
          value={boundProgress(progressPercent)}
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: 2,
            bgcolor: "#e6e9f2",
            "& .MuiLinearProgress-bar": { bgcolor: "#0b2ea1" },
          }}
        />
      )}
    </Box>
  );
}
